import { useMemo } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  ContractsContractHistoryDocument,
  JobCatalogTreeLevelNodeFragment,
} from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  findGlobalOngoingActivityPeriod,
  getContractTitle,
} from 'modules/contracts/lib/ContractHistoryUtils'

import ContractView from './ContractView'
import LoadingState from './LoadingState'

interface Props {
  open: boolean
  employeeId: string
  onContractVersionSelect: (contractVersionId: string | undefined) => void
  selectedContractVersionId: string | undefined
  toggleHistory?: () => void
}

const NewContractHistory = ({
  open,
  employeeId,
  onContractVersionSelect,
  selectedContractVersionId,
  toggleHistory,
}: Props) => {
  const { data, loading } = useQuery(ContractsContractHistoryDocument, {
    variables: { employeeId },
  })
  const contracts = data?.contracts?.contractsConnection?.nodes || []

  const jobCatalogTreeLevelNodes: JobCatalogTreeLevelNodeFragment[] = useMemo(() => {
    const seen = new Set<string>()
    const nodes: JobCatalogTreeLevelNodeFragment[] = []
    for (const contract of contracts) {
      for (const version of contract.contractVersions ?? []) {
        const level = version.jobCatalogTreeLevel
        if (level?.uuid && !seen.has(level.uuid)) {
          seen.add(level.uuid)
          nodes.push(level)
        }
      }
    }
    return nodes
  }, [contracts])

  if (contracts.length === 0 || loading) {
    return (
      <Sidepanel
        open={open}
        title={i18n.t('contract_versions.header.history')}
        width="wide"
        onClose={toggleHistory}
      >
        <Box flexDirection="column" alignItems="center" justifyContent="center" flexGrow>
          <LoadingState />
        </Box>
      </Sidepanel>
    )
  }

  const globalOngoingActivityPeriod = findGlobalOngoingActivityPeriod(contracts)

  const handleContractVersionSelect = (referenceVersionId: string | undefined) => {
    if (referenceVersionId) {
      onContractVersionSelect(referenceVersionId)
      toggleHistory?.()
    }
  }

  return (
    <Sidepanel
      open={open}
      width="wide"
      title={i18n.t('contract_versions.header.history')}
      onClose={toggleHistory}
    >
      <Box flexDirection="column" gap="s48" width="full">
        {contracts.map((contract, index) => (
          <Box key={contract.id} flexDirection="column" gap="s8" width="full">
            <Box flexDirection="column">
              <Text.Normal size="100" weight="medium" color="grey1000">
                {getContractTitle({ contract }).title}
              </Text.Normal>
              <Text.Normal size="100" weight="medium">
                {getContractTitle({ contract }).subtitle}
              </Text.Normal>
            </Box>
            <ContractView
              key={contract.id}
              contract={contract}
              firstContract={index === contracts.length - 1}
              onContractVersionSelect={handleContractVersionSelect}
              selectedContractVersionId={selectedContractVersionId}
              contractOngoingActivityPeriod={globalOngoingActivityPeriod}
              jobCatalogTreeLevelNodes={jobCatalogTreeLevelNodes}
            />
          </Box>
        ))}
      </Box>
    </Sidepanel>
  )
}

export default NewContractHistory
